import { Icon, Notice, ProductCard, Text, HStack, VStack } from '@genial/components';
import { useNavigation } from '@react-navigation/native';
import { IconButton, Button, Pressable, Box } from 'native-base';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import { checkingsAccountDataQuery } from '../../../../store/managementAnalysis/selector';
import { masterState } from '../../../../store/master/atom';

export interface CheckingAccountCardProps {
  isMobile?: boolean;
  hideRightElement?: boolean;
  investmentResumeLoading: boolean;
}

export const CheckingAccountCard = ({
  isMobile,
  hideRightElement,
  investmentResumeLoading,
}: CheckingAccountCardProps) => {
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { selectedAdvisor } = useRecoilValue(masterState);
  const isAllAdvisors = selectedAdvisor === 0;

  const {
    loading: dataLoading,
    error: hasError,
    data,
    refresher,
  } = useRecoilQuery(checkingsAccountDataQuery);
  const loading = dataLoading || investmentResumeLoading;

  const handleRefreshPress = () => {
    refresher?.();
  };

  const handleChevronPress = () => {
    navigate('ManagementAnalysisDetails', {
      product: {
        color: 'product.contacorrente',
        label: 'Conta Corrente',
        productClassName: 'checking-account',
        productClassPercentage: 0,
        productClassPercentageFormatted: '0%',
        productClassValue: data?.availableBalance || 0,
        productClassValueFormatted: data?.availableBalanceNormalized || '',
      },
      alertText: '',
      distributionProductInformation: {
        executionDate: data?.updatedAt,
      },
    });
  };

  const renderDataInfo = () => {
    if (isAllAdvisors) {
      return (
        <Box flex={1} maxW={{ base: '100%', sm: '60%' }} alignSelf="flex-end">
          <Notice
            textObjects={[
              {
                text: 'Atenção: ',
                variant: 'semibold',
              },
              {
                text: (
                  <>
                    A visualização <Text fontWeight="semibold">Todas as filiais</Text> ou{' '}
                    <Text fontWeight="semibold">Todos os assessores</Text> não está disponível para
                    CONTA CORRENTE. Selecione sua filial inicial e o assessor ao qual o cliente está
                    alocado.
                  </>
                ),
                variant: 'regular',
              },
            ]}
          />
        </Box>
      );
    }

    return (
      <HStack alignItems="center" space="inline.nano">
        <Text color="neutral.medium">{`Dados atualizados ${data?.updatedAtNormalized || ''}`}</Text>
        <IconButton
          p={0}
          variant="unstyled"
          onPress={handleRefreshPress}
          icon={
            <Icon
              name="Refresh"
              size="xxxxs"
              color="brand.tertiary.principal"
              testId="refresh-icon-checking-account"
            />
          }
        />
      </HStack>
    );
  };

  if (hasError) {
    return (
      <VStack
        alignItems="center"
        borderLeftColor="product.contacorrente"
        borderLeftWidth="4"
        borderLeftRadius="xs">
        <VStack alignItems="center" space="stack.nano">
          <Icon name="Warning" testId="error-icon" color="feedback.attention.default" size="md" />
          <Text fontSize="lg" variant="semibold" color="neutral.black">
            Ops, Algo deu errado!!
          </Text>
          <Text fontSize="md" color="neutral.black">
            No momento não conseguimos te mostrar os resultados de conta corrente.
          </Text>
          <Button
            justifyContent="flex-start"
            size="lg"
            variant="textLink"
            rightIcon={
              <Icon name="Refresh" size="xxs" testId="aa" color="brand.tertiary.principal" />
            }
            onPress={handleRefreshPress}>
            Recarregar
          </Button>
        </VStack>
      </VStack>
    );
  }

  if (isMobile) {
    return (
      <Pressable onPress={handleChevronPress}>
        <ProductCard
          loading={loading}
          testId="checking-account"
          color="product.contacorrente"
          product={{
            name: 'CONTA CORRENTE',
            percentage: 0,
            value: data?.availableBalance || 0,
          }}
          stackProps={{ py: 'stack.xxs' }}
          mobileBottomComponent={renderDataInfo()}
          hidePercentage
          hideValue={isAllAdvisors}
        />
      </Pressable>
    );
  }

  return (
    <ProductCard
      loading={loading}
      testId="checking-account"
      color="product.contacorrente"
      product={{
        name: 'CONTA CORRENTE',
        percentage: 0,
        value: data?.availableBalance || 0,
      }}
      hidePercentage
      hideValue={isAllAdvisors}
      stackProps={{ py: 'stack.xs' }}
      leftComponent={renderDataInfo()}
      rightComponent={
        !hideRightElement &&
        !isAllAdvisors && (
          <IconButton
            p={0}
            pl="inline.nano"
            variant="unstyled"
            onPress={handleChevronPress}
            icon={
              <Icon testId="-chevron-icon" name="ChevronRight" size="xxs" color="neutral.black" />
            }
          />
        )
      }
    />
  );
};
